import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe, Layers, Lock } from 'lucide-react';
import { useToast } from './ui/Toast';
import OsScroll from './ui/OsScroll';
import VariableKvTable from './ui/VariableKvTable';
import { envInitials } from '../utils/envAvatar';
import {
  stripBlankKvRows,
  makeKvRowId,
  ensureTrailingEmptyKvRow,
} from '../utils/kvRows';

/** Same as RequestBuilder params / headers key-value inputs */
const REQUEST_BUILDER_KV_INPUT_CLASS =
  'request-builder-kv-input w-full rounded-md focus:outline-none';
const REQUEST_BUILDER_KV_INPUT_STYLE = { minHeight: '36px' };

function variablesWithIds(rawVariables, timestamp) {
  const mapped = stripBlankKvRows(rawVariables || []).map((v, i) => ({
    ...v,
    secret: v.secret ?? false,
    _id: v._id || `env-${i}-${timestamp}`,
  }));
  return ensureTrailingEmptyKvRow(mapped, 'env');
}

const EnvironmentEditor = ({
  initialData,
  onDataChange,
  onSave,
  onRegisterSave,
  isGlobal = false,
  isWorkspaceVariables = false,
  selectedEnvironment = null,
  workspaceVariables = [],
  globalVariables = [],
  collectionVariables = [],
  currentWorkspaceId = null,
  onVariableUpdate,
}) => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const initialTimestamp = useRef(Date.now());

  // Track which secret rows are temporarily revealed (show plain text)
  const nameInputRef = useRef(null);

  const [localData, setLocalData] = useState(() => ({
    id: initialData?.id || null,
    name:
      initialData?.name ||
      (isWorkspaceVariables ? 'Workspace Variables' : isGlobal ? 'Global Variables' : 'New Environment'),
    variables: variablesWithIds(initialData?.variables, initialTimestamp.current),
  }));

  useEffect(() => {
    if (initialData) {
      const timestamp = Date.now();
      setLocalData({
        ...initialData,
        variables: variablesWithIds(initialData.variables, timestamp),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reseeds the draft only when a different environment is opened; depending on the whole object would wipe in-progress edits on every parent render
  }, [initialData?.id]);

  const notifyParent = useCallback(
    (next) => {
      if (!onDataChange) return;
      const cleanData = {
        ...next,
        variables: stripBlankKvRows(
          next.variables.map(({ _id, ...variable }) => variable)
        ),
      };
      if (isGlobal) cleanData.isGlobal = true;
      if (isWorkspaceVariables) cleanData.isWorkspaceVariables = true;
      onDataChange(cleanData);
    },
    [onDataChange, isGlobal, isWorkspaceVariables]
  );

  /** Avoid updating App state synchronously inside setState updaters. */
  const scheduleNotifyParent = useCallback(
    (next) => {
      queueMicrotask(() => notifyParent(next));
    },
    [notifyParent]
  );

  const variableContext = useMemo(
    () => ({ workspaceId: currentWorkspaceId }),
    [currentWorkspaceId]
  );

  /** Defer trailing-row append — same cadence as RequestBuilder params `handleParamKeyChange`. */
  const handleVariableKeyChange = (index, value) => {
    setLocalData((prev) => {
      const vars = [...prev.variables];
      const prevKey = vars[index].key;
      vars[index] = { ...vars[index], key: value };
      const next = { ...prev, variables: vars };
      scheduleNotifyParent(next);

      const shouldAppendTrailing =
        index === prev.variables.length - 1 && prevKey === '' && value.length > 0;

      if (shouldAppendTrailing) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setLocalData((p) => {
              const last = p.variables[p.variables.length - 1];
              if (!last || last.key === '') return p;
              const withTrailing = {
                ...p,
                variables: [
                  ...p.variables,
                  { key: '', value: '', enabled: true, _id: makeKvRowId('env') },
                ],
              };
              scheduleNotifyParent(withTrailing);
              return withTrailing;
            });
          });
        });
      }

      return next;
    });
  };

  /** `patch` is an object merged into the row — see VariableKvTable's onUpdate. */
  const updateVariable = (index, patch) => {
    setLocalData((prev) => {
      const vars = [...prev.variables];
      vars[index] = { ...vars[index], ...patch };
      const next = { ...prev, variables: vars };
      scheduleNotifyParent(next);
      return next;
    });
  };

  const removeVariable = (index) => {
    setLocalData((prev) => {
      let vars = prev.variables.filter((_, i) => i !== index);
      if (vars.length === 0) {
        vars = [{ key: '', value: '', enabled: true, _id: `env-0-${Date.now()}` }];
      } else {
        vars = ensureTrailingEmptyKvRow(vars, 'env');
      }
      const next = { ...prev, variables: vars };
      scheduleNotifyParent(next);
      return next;
    });
  };

  // Resolves false when nothing was saved (so the modal footer does not say "Saved").
  const handleSave = useCallback(async () => {
    if (!isGlobal && !isWorkspaceVariables && (!localData.name || localData.name.trim() === '')) {
      showToast(t('environment.nameRequired'), 'error');
      return false;
    }

    if (onSave) {
      const cleanData = {
        ...localData,
        variables: stripBlankKvRows(
          localData.variables.map(({ _id, ...variable }) => variable)
        ),
      };
      if (isGlobal) cleanData.isGlobal = true;
      if (isWorkspaceVariables) cleanData.isWorkspaceVariables = true;
      const saved = await onSave(cleanData);
      // false = refused (Plans v2: a new environment past the plan's cap, or a
      // read-only cloud workspace; the upgrade is open). An environment save
      // otherwise answers with the environment; globals and workspace
      // variables answer null.
      if (saved === false || (!isGlobal && !isWorkspaceVariables && saved == null)) return false;
      showToast(
        isGlobal
          ? t('environment.savedGlobal')
          : isWorkspaceVariables
            ? t('environment.savedWorkspace')
            : t('environment.savedEnvironment', { name: localData.name }),
        'success'
      );
    }
  }, [isGlobal, isWorkspaceVariables, localData, onSave, showToast, t]);

  // Expose the save handler so the modal footer can trigger it.
  useEffect(() => {
    onRegisterSave?.(handleSave);
  }, [onRegisterSave, handleSave]);

  useEffect(() => {
    if (isGlobal || isWorkspaceVariables || initialData?.id || localData.name) return;
    nameInputRef.current?.focus();
  }, [initialData?.id, isGlobal, isWorkspaceVariables, localData.name]);

  return (
    <div className="flex-1 flex flex-col h-full min-h-0 bg-white dark:bg-[#161618]">
      <OsScroll className="flex-1 min-h-0 bg-white dark:bg-[#161618] px-8 py-7 editor-tab-body">
        <div className="env-editor-head">
          {!isGlobal && !isWorkspaceVariables ? (
            <div
              className="env-editor-name-field"
              onClick={() => nameInputRef.current?.focus()}
            >
              <span className="env-avatar">
                {envInitials(localData.name)}
              </span>
              <input
                ref={nameInputRef}
                type="text"
                value={localData.name}
                onChange={(e) => {
                  setLocalData((prev) => {
                    const next = { ...prev, name: e.target.value };
                    scheduleNotifyParent(next);
                    return next;
                  });
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === 'Escape') {
                    e.preventDefault();
                    e.currentTarget.blur();
                  }
                }}
                placeholder={t('environment.enterEnvironmentName')}
                className="env-editor-name-input"
                aria-label={t('environment.environmentName')}
              />
            </div>
          ) : (
            <div className="env-editor-title-static">
              {isWorkspaceVariables ? (
                <Layers size={20} className="env-editor-title-icon env-editor-title-icon--workspace" aria-hidden />
              ) : (
                <Globe size={20} className="env-editor-title-icon env-editor-title-icon--global" aria-hidden />
              )}
              <span>
                {isGlobal
                  ? t('environment.globalVariablesTitle')
                  : t('environment.workspaceVariablesTitle')}
              </span>
            </div>
          )}
        </div>

        <div className="env-editor-section">
          <h2 className="env-editor-section-title">Variables</h2>
          <p className="env-var-secret-hint">
            <Lock size={11} aria-hidden />
            {t('environment.secretHint')}
          </p>
        </div>

        <VariableKvTable
          variables={localData.variables}
          onUpdate={updateVariable}
          onRemove={removeVariable}
          onKeyChange={handleVariableKeyChange}
          inputClassName={REQUEST_BUILDER_KV_INPUT_CLASS}
          inputStyle={REQUEST_BUILDER_KV_INPUT_STYLE}
          menuClassName="env-option-menu"
          inputProps={{
            selectedEnvironment,
            workspaceVariables,
            globalVariables,
            collectionVariables,
            onVariableUpdate,
            variableContext,
          }}
        />
      </OsScroll>
    </div>
  );
};

export default EnvironmentEditor;
